import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Plus, Minus, Sun, Moon, Search } from "lucide-react";
import { api, recipeImage } from "@/lib/api";
import { weekDays, todayStr, prettyDate } from "@/lib/dates";
import { useAuth } from "@/context/AuthContext";

export default function AddMealDialog({ open, onOpenChange, recipe, fixedDate, fixedSlot, onAdded }) {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState([]);
  const [chosenRecipe, setChosenRecipe] = useState(recipe || null);
  const [date, setDate] = useState(fixedDate || todayStr());
  const [slot, setSlot] = useState(fixedSlot || "soir");
  const [servings, setServings] = useState(user?.household_size || recipe?.servings || 2);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const days = [{ label: "Aujourd'hui", date: todayStr() }, ...weekDays()];
  const uniqueDays = days.filter((d, i, arr) => arr.findIndex((x) => x.date === d.date) === i);

  useEffect(() => {
    if (open) {
      setChosenRecipe(recipe || null);
      setDate(fixedDate || todayStr());
      setSlot(fixedSlot || "soir");
      setServings(user?.household_size || recipe?.servings || 2);
      if (!recipe) api.get("/recipes").then((r) => setRecipes(r.data));
    }
  }, [open, recipe, fixedDate, fixedSlot]);

  const submit = async () => {
    if (!chosenRecipe) { toast.error("Choisissez une recette"); return; }
    setSaving(true);
    try {
      await api.post("/meals", { recipe_id: chosenRecipe.id, date, slot, servings: Number(servings) });
      toast.success("Repas ajouté au planning");
      onOpenChange(false);
      onAdded && onAdded();
    } catch {
      toast.error("Erreur lors de l'ajout");
    } finally {
      setSaving(false);
    }
  };

  const filtered = recipes.filter((r) => r.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-3xl" data-testid="add-meal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {fixedSlot ? `${prettyDate(fixedDate)} • ${fixedSlot === "midi" ? "Midi" : "Soir"}` : "Ajouter au planning"}
          </DialogTitle>
          <DialogDescription className="sr-only">Choisir une recette, un jour, un créneau et le nombre de personnes</DialogDescription>
        </DialogHeader>

        {!recipe && (
          <div className="space-y-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-3" style={{ color: "var(--at-muted)" }} />
              <Input data-testid="meal-recipe-search" placeholder="Chercher une recette..." value={search}
                onChange={(e) => setSearch(e.target.value)} className="rounded-2xl border-black/10 pl-9" />
            </div>
            <div className="max-h-52 space-y-2 overflow-y-auto">
              {filtered.length === 0 && <p className="py-4 text-center text-sm" style={{ color: "var(--at-muted)" }}>Aucune recette. Scannez-en une d'abord !</p>}
              {filtered.map((r) => (
                <button key={r.id} data-testid={`meal-pick-recipe-${r.id}`} onClick={() => { setChosenRecipe(r); setServings(user?.household_size || r.servings || 2); }}
                  className={`flex w-full items-center gap-3 rounded-2xl border p-2 text-left at-press ${chosenRecipe?.id === r.id ? "border-2" : "border-black/5"}`}
                  style={{ borderColor: chosenRecipe?.id === r.id ? "var(--at-brand)" : undefined }}>
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl" style={{ background: "var(--at-elevated)" }}>
                    {recipeImage(r) && <img src={recipeImage(r)} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <span className="font-medium">{r.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {recipe && (
          <div className="rounded-2xl bg-[var(--at-elevated)] p-3 font-medium">{recipe.title}</div>
        )}

        {!fixedDate && (
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Quel jour ?</p>
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
              {uniqueDays.map((d) => (
                <button key={d.date} data-testid={`meal-day-${d.date}`} onClick={() => setDate(d.date)}
                  className={`shrink-0 rounded-2xl border px-4 py-2 text-sm font-medium at-press ${date === d.date ? "text-white" : ""}`}
                  style={date === d.date ? { background: "var(--at-brand)", borderColor: "var(--at-brand)" } : { borderColor: "var(--at-border)" }}>
                  {d.label} {d.date !== todayStr() && d.label !== "Aujourd'hui" ? "" : ""}
                </button>
              ))}
            </div>
          </div>
        )}

        {!fixedSlot && (
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="meal-slot-midi" onClick={() => setSlot("midi")}
              className={`flex items-center justify-center gap-2 rounded-2xl border py-3 font-medium at-press ${slot === "midi" ? "text-white" : ""}`}
              style={slot === "midi" ? { background: "var(--at-honey)", borderColor: "var(--at-honey)" } : { borderColor: "var(--at-border)" }}>
              <Sun size={18} /> Midi
            </button>
            <button data-testid="meal-slot-soir" onClick={() => setSlot("soir")}
              className={`flex items-center justify-center gap-2 rounded-2xl border py-3 font-medium at-press ${slot === "soir" ? "text-white" : ""}`}
              style={slot === "soir" ? { background: "#4A4E7A", borderColor: "#4A4E7A" } : { borderColor: "var(--at-border)" }}>
              <Moon size={18} /> Soir
            </button>
          </div>
        )}

        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--at-muted)" }}>Nombre de personnes</p>
          <div className="flex items-center justify-center gap-4 rounded-2xl border border-black/10 py-2">
            <Button size="icon" variant="ghost" className="h-10 w-10 rounded-full" data-testid="meal-servings-minus"
              onClick={() => setServings((s) => Math.max(1, s - 1))}><Minus size={18} /></Button>
            <span className="w-8 text-center text-xl font-bold" data-testid="meal-servings-value">{servings}</span>
            <Button size="icon" variant="ghost" className="h-10 w-10 rounded-full" data-testid="meal-servings-plus"
              onClick={() => setServings((s) => s + 1)}><Plus size={18} /></Button>
          </div>
        </div>

        <Button data-testid="confirm-add-meal-btn" onClick={submit} disabled={saving}
          className="h-13 w-full rounded-2xl py-3 text-base font-semibold text-white at-press" style={{ background: "var(--at-brand)" }}>
          {saving ? "Ajout..." : "Ajouter au planning"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
